import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from './ChatMessage';
import { chatService } from '../../services/chat.service';
import { useAppStore } from '../../store/appStore';
import { apiClient } from '../../services/api';
import styles from './chat.module.css';

interface BookingData {
  boat_model: string;
  boat_length: number;
  boat_width: number;
  boat_draft: number;
  check_in_date: string;
  check_out_date: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  mooring_name?: string;
  notes?: string;
}

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

export const ChatWidget: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState<BookingData | null>(null);
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartTimeRef = useRef<number>(0);
  const recordingMimeTypeRef = useRef<string>('audio/webm');

  // Store state
  const messages = useAppStore((state) => state.messages);
  const isLoadingChat = useAppStore((state) => state.isLoadingChat);
  const chatError = useAppStore((state) => state.chatError);
  const isRecording = useAppStore((state) => state.isRecording);
  const audioElement = useAppStore((state) => state.audioElement);
  const isChangingLanguage = useAppStore((state) => state.isChangingLanguage);

  const addMessage = useAppStore((state) => state.addMessage);
  const setLoadingChat = useAppStore((state) => state.setLoadingChat);
  const setChatError = useAppStore((state) => state.setChatError);
  const clearMessages = useAppStore((state) => state.clearMessages);
  const setAvatarSpeaking = useAppStore((state) => state.setAvatarSpeaking);
  const setAvatarLoading = useAppStore((state) => state.setAvatarLoading);
  const setCurrentVisemes = useAppStore((state) => state.setCurrentVisemes);
  const setAudioElement = useAppStore((state) => state.setAudioElement);
  const setRecording = useAppStore((state) => state.setRecording);
  const setViewMode = useAppStore((state) => state.setViewMode);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Block body scroll when modal is open (important for mobile)
  useEffect(() => {
    console.log('[ChatWidget] showBookingModal changed:', showBookingModal);
    console.log('[ChatWidget] pendingBookingData:', pendingBookingData ? 'present' : 'null');

    if (showBookingModal) {
      console.log('[ChatWidget] ✅ Modal opened - blocking body scroll');
      console.log('[ChatWidget] Modal data:', pendingBookingData);
      document.body.style.overflow = 'hidden';

      // Add a visual indicator to body for debugging
      document.body.setAttribute('data-modal-open', 'true');
    } else {
      console.log('[ChatWidget] ❌ Modal closed - restoring body scroll');
      document.body.style.overflow = '';
      document.body.removeAttribute('data-modal-open');
    }

    return () => {
      document.body.style.overflow = '';
      document.body.removeAttribute('data-modal-open');
    };
  }, [showBookingModal, pendingBookingData]);

  // Stop current audio before playing new one (prevents overlapping)
  const stopCurrentAudio = () => {
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setAudioElement(null);
      setAvatarSpeaking(false);
      setCurrentVisemes([]);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputValue.trim()) return;

    // CRITICAL: Block if language change is in progress
    if (isChangingLanguage) {
      console.log('[ChatWidget] ❌ BLOCKED - Cannot send message during language change');
      return;
    }

    // Stop any currently playing audio before sending new message
    stopCurrentAudio();

    const userInput = inputValue;
    setInputValue('');
    setChatError(null);

    // Create audio element immediately in user interaction context for mobile autoplay
    const audioPlaceholder = new Audio();
    audioPlaceholder.src = 'data:audio/mpeg;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA//////////////////////////////////////////////////////////////////8AAABQTEFNRTMuMTAwBLkAAAAAAAAAABUgJAUHQQABzAAABQGmzQb0AAAAAAD/+xDEAAPAAAGkAAAAIAAANIAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
    console.log('[ChatWidget] 📱 Created audio placeholder for mobile autoplay compatibility');

    try {
      setLoadingChat(true);

      // Send message to AI with current language
      const response = await chatService.sendMessage(userInput, i18n.language);

      // Add user message to UI
      addMessage({
        id: `msg-${Date.now()}`,
        role: 'user',
        content: userInput,
        timestamp: Date.now(),
      });

      // Add assistant message to UI
      addMessage(response);

      // Check if booking is ready - show modal if so
      if (response.booking_ready && response.booking_data) {
        const bookingData = response.booking_data;
        console.log('✅ Booking ready! Extracted data from chat:');
        console.log('  - Name:', bookingData.contact_name);
        console.log('  - Email:', bookingData.contact_email);
        console.log('  - Phone:', bookingData.contact_phone);
        console.log('  - Boat:', bookingData.boat_model, `(${bookingData.boat_length}x${bookingData.boat_width}x${bookingData.boat_draft}m)`);
        console.log('  - Dates:', bookingData.check_in_date, 'to', bookingData.check_out_date);
        console.log('  - Mooring:', bookingData.mooring_name);
        console.log('  - Notes:', bookingData.notes);
        setPendingBookingData(bookingData);
        setShowBookingModal(true);
        console.log('[ChatWidget] 🎯 Showing booking modal on mobile - check if visible');
      }

      // Synthesize audio and animate avatar
      try {
        console.log('[ChatWidget] 🎤 Starting TTS synthesis for text:', response.content.substring(0, 50) + '...');
        setAvatarLoading(true);

        const ttsResponse = await apiClient.synthesize({
          text: response.content,
          language: i18n.language,
        });

        console.log('[ChatWidget] ✅ TTS synthesis completed');
        console.log('[ChatWidget] - Audio received:', !!ttsResponse.audio_base64);
        console.log('[ChatWidget] - Visemes count:', ttsResponse.visemes?.length || 0);
        console.log('[ChatWidget] - Duration:', ttsResponse.duration);

        setAvatarLoading(false);
        setAvatarSpeaking(true);

        // Update visemes in store
        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          console.log('[ChatWidget] 📊 Visemes received:', ttsResponse.visemes.length);
          setCurrentVisemes(ttsResponse.visemes);
        } else {
          console.warn('[ChatWidget] ⚠️ No visemes received from TTS');
        }

        // Play audio and store reference for avatar sync
        if (ttsResponse.audio_base64) {
          console.log('[ChatWidget] 🔊 Setting audio src and playing...');

          // Use the audio placeholder created in user interaction context
          audioPlaceholder.src = `data:audio/mpeg;base64,${ttsResponse.audio_base64}`;

          // Store audio element in app state so Avatar can read currentTime directly
          setAudioElement(audioPlaceholder);

          audioPlaceholder.onplay = () => {
            console.log('[ChatWidget] ▶️ Audio started playing (mobile-compatible)');
          };

          audioPlaceholder.onended = () => {
            console.log('[ChatWidget] ⏹️ Audio ended');
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audioPlaceholder.onerror = (error) => {
            console.error('[ChatWidget] ❌ Audio error:', error);
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audioPlaceholder.play().catch((error) => {
            console.error('[ChatWidget] ❌ Audio playback error:', error);
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          });
        } else {
          console.warn('[ChatWidget] ⚠️ No audio_base64 in TTS response');
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
        }
      } catch (ttsError) {
        console.error('[ChatWidget] ❌ TTS error:', ttsError);
        setAvatarSpeaking(false);
        setCurrentVisemes([]);
        // Don't show error to user, chat still worked
      }
    } catch (error) {
      console.error('Chat error:', error);
      setChatError(t('chat.error'));
    } finally {
      setLoadingChat(false);
    }
  };

  const handleClearChat = () => {
    clearMessages();
    chatService.clearHistory();
  };

  const handleConfirmBooking = async () => {
    if (!pendingBookingData) return;

    try {
      setIsSubmittingBooking(true);
      setChatError(null);

      // Log booking data being sent for debugging
      console.log('📤 Sending booking data to backend:');
      console.log('  - Name:', pendingBookingData.contact_name);
      console.log('  - Email:', pendingBookingData.contact_email);
      console.log('  - Phone:', pendingBookingData.contact_phone);
      console.log('  - Boat:', pendingBookingData.boat_model, `(${pendingBookingData.boat_length}x${pendingBookingData.boat_width}x${pendingBookingData.boat_draft}m)`);
      console.log('  - Dates:', pendingBookingData.check_in_date, 'to', pendingBookingData.check_out_date);
      console.log('  - Mooring:', pendingBookingData.mooring_name);
      console.log('  - Notes:', pendingBookingData.notes);

      // Send booking to backend
      const bookingResponse = await apiClient.submitChatBooking(pendingBookingData);

      // Close modal
      setShowBookingModal(false);
      setPendingBookingData(null);

      // Add confirmation message to chat
      const confirmationMessage = `
✅ ${t('booking.confirmation.title')}

${t('booking.confirmation.boat')}: ${pendingBookingData.boat_model}
${t('booking.confirmation.checkIn')}: ${formatDate(pendingBookingData.check_in_date)}
${t('booking.confirmation.checkOut')}: ${formatDate(pendingBookingData.check_out_date)}
${t('booking.confirmation.contact')}: ${pendingBookingData.contact_name}

${t('booking.confirmation.emailNotice')}
      `.trim();

      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: confirmationMessage,
        timestamp: Date.now(),
      });

      console.log('✅ Booking confirmed:', bookingResponse);

      // Synthesize confirmation message audio
      try {
        setAvatarLoading(true);

        const ttsResponse = await apiClient.synthesize({
          text: confirmationMessage,
          language: i18n.language,
        });

        setAvatarLoading(false);
        setAvatarSpeaking(true);

        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          setCurrentVisemes(ttsResponse.visemes);
        }

        if (ttsResponse.audio_base64) {
          const audio = new Audio(`data:audio/mpeg;base64,${ttsResponse.audio_base64}`);
          setAudioElement(audio);

          audio.onended = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);

            // Return to initial screen after confirmation message
            setTimeout(() => {
              console.log('[ChatWidget] 🔄 Returning to initial screen after booking confirmation');
              clearMessages();
              chatService.clearHistory();
              setViewMode('initial');
            }, 3000);
          };

          audio.onerror = () => {
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);

            // Return to initial screen even on error
            setTimeout(() => {
              console.log('[ChatWidget] 🔄 Returning to initial screen after audio error');
              clearMessages();
              chatService.clearHistory();
              setViewMode('initial');
            }, 3000);
          };

          audio.play().catch((error) => {
            console.error('Audio playback error:', error);
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);

            // Return to initial screen even on play error
            setTimeout(() => {
              console.log('[ChatWidget] 🔄 Returning to initial screen after play error');
              clearMessages();
              chatService.clearHistory();
              setViewMode('initial');
            }, 3000);
          });
        } else {
          setAvatarSpeaking(false);
          setCurrentVisemes([]);

          // Return to initial screen even without audio
          setTimeout(() => {
            console.log('[ChatWidget] 🔄 Returning to initial screen (no audio)');
            clearMessages();
            chatService.clearHistory();
            setViewMode('initial');
          }, 3000);
        }
      } catch (ttsError) {
        console.error('TTS error for confirmation:', ttsError);
        setAvatarSpeaking(false);
        setAvatarLoading(false);

        // Return to initial screen even if TTS fails (booking was successful)
        setTimeout(() => {
          console.log('[ChatWidget] 🔄 Returning to initial screen after TTS error');
          clearMessages();
          chatService.clearHistory();
          setViewMode('initial');
        }, 3000);
      }
    } catch (error: any) {
      console.error('Booking error:', error);
      const errorMessage = error.response?.data?.detail || error.message || t('chat.error');
      setChatError(`${t('chat.error')}: ${errorMessage}`);
    } finally {
      setIsSubmittingBooking(false);
    }
  };

  const startRecording = async () => {
    console.log('🎤 startRecording called');
    try {
      // Stop any existing recording first
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        console.log('⚠️ Stopping existing recording before starting new one');
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }

      // Clean state
      audioChunksRef.current = [];
      mediaRecorderRef.current = null;

      // Check if MediaDevices API is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const errorMsg = t('chat.audioNotSupported');
        setChatError(errorMsg);
        console.error('❌ MediaDevices API not supported');
        alert(errorMsg); // Show alert on mobile for visibility
        return;
      }

      console.log('✅ MediaDevices API is supported');

      // Request microphone permission with better error handling
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
      } catch (permissionError: any) {
        console.error('❌ Microphone permission error:', permissionError);
        console.error('Error name:', permissionError.name);
        console.error('Error message:', permissionError.message);

        let errorMsg = '';
        if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
          errorMsg = t('chat.microphonePermissionDenied');
        } else if (permissionError.name === 'NotFoundError') {
          errorMsg = t('chat.microphoneNotFound');
        } else if (permissionError.name === 'NotReadableError') {
          errorMsg = t('chat.microphoneInUse');
        } else {
          errorMsg = t('chat.audioError');
        }

        setChatError(errorMsg);
        alert(errorMsg + '\n\nError: ' + permissionError.message); // Show on mobile
        return;
      }

      console.log('✅ Microphone permission granted');

      // Determine the best MIME type for mobile compatibility
      let mimeType = 'audio/webm;codecs=opus';

      // Check supported MIME types for mobile compatibility
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        if (MediaRecorder.isTypeSupported('audio/mp4')) {
          mimeType = 'audio/mp4';
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
          mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
          mimeType = 'audio/ogg;codecs=opus';
        } else {
          // Let the browser use its default
          mimeType = '';
        }
      }

      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);

      // Save the actual mimeType being used (important for Safari iOS)
      const actualMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
      recordingMimeTypeRef.current = actualMimeType;
      console.log('📝 Using mimeType:', actualMimeType);

      mediaRecorder.ondataavailable = (event) => {
        console.log('📼 Data available, chunk size:', event.data.size);
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('✅ Chunk added. Total chunks:', audioChunksRef.current.length);
        } else {
          console.warn('⚠️ Received empty chunk');
        }
      };

      mediaRecorder.onerror = (event: any) => {
        console.error('MediaRecorder error:', event.error);
        setChatError(t('chat.recordingError'));
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        setRecording(false);
      };

      // Safari iOS fix: use timeslice to force data collection
      // On Safari, if you don't specify a timeslice, ondataavailable might not be called
      const timeslice = 100; // Collect data every 100ms
      mediaRecorder.start(timeslice);

      mediaRecorderRef.current = mediaRecorder;
      recordingStartTimeRef.current = Date.now();
      setRecording(true);
      console.log('✅ Recording started successfully with mimeType:', mimeType || 'default', 'timeslice:', timeslice);
    } catch (error: any) {
      console.error('❌ Failed to start recording:', error);
      console.error('Error details:', error.message, error.stack);
      const errorMsg = t('chat.audioError') + ': ' + (error.message || 'Unknown error');
      setChatError(errorMsg);
      alert(errorMsg); // Show on mobile
    }
  };

  const stopRecording = async () => {
    return new Promise<void>((resolve) => {
      if (!mediaRecorderRef.current) {
        console.log('⚠️ No mediaRecorder found');
        resolve();
        return;
      }

      const currentRecorder = mediaRecorderRef.current;
      const recordingDuration = Date.now() - recordingStartTimeRef.current;

      console.log('🛑 Stopping recording, chunks collected:', audioChunksRef.current.length);
      console.log('⏱️ Recording duration:', recordingDuration, 'ms');

      // Check minimum recording time
      if (recordingDuration < 300) {
        console.warn('⚠️ Recording too short:', recordingDuration, 'ms. Minimum is 300ms');
        setChatError('Registrazione troppo breve. Tieni premuto più a lungo.');

        // Clean up
        if (currentRecorder.stream) {
          currentRecorder.stream.getTracks().forEach((track) => track.stop());
        }
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        setRecording(false);
        resolve();
        return;
      }

      currentRecorder.onstop = async () => {
        console.log('📦 Creating audio blob from', audioChunksRef.current.length, 'chunks');

        // Create blob from collected chunks - USE THE CORRECT MIME TYPE!
        const blobMimeType = recordingMimeTypeRef.current;
        const audioBlob = new Blob(audioChunksRef.current, { type: blobMimeType });
        console.log('📦 Blob size:', audioBlob.size, 'bytes, type:', blobMimeType);

        // Stop all audio tracks before processing
        if (currentRecorder.stream) {
          currentRecorder.stream.getTracks().forEach((track) => {
            console.log('🔇 Stopping track:', track.kind);
            track.stop();
          });
        }

        // Reset for next recording
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
        setRecording(false);

        // Process the audio - ensure minimum blob size (at least 1KB)
        const minBlobSize = 1024; // 1KB minimum
        if (audioBlob.size > minBlobSize) {
          const reader = new FileReader();

          reader.onload = async () => {
            const audioBase64 = (reader.result as string).split(',')[1];
            console.log('✅ Audio ready for transcription, base64 length:', audioBase64.length);
            await handleAudioRecording(audioBase64);
            resolve();
          };

          reader.onerror = () => {
            console.error('❌ FileReader error');
            setChatError(t('chat.recordingError'));
            resolve();
          };

          reader.readAsDataURL(audioBlob);
        } else {
          console.error('❌ Audio blob too small:', audioBlob.size, 'bytes. Minimum:', minBlobSize);
          setChatError('Audio troppo breve o vuoto. Parla più a lungo e più chiaramente.');
          resolve();
        }
      };

      currentRecorder.stop();
    });
  };

  const handleAudioRecording = async (audioBase64: string) => {
    // CRITICAL: Block if language change is in progress
    if (isChangingLanguage) {
      console.log('[handleAudioRecording] ❌ BLOCKED - Cannot process audio during language change');
      return;
    }

    try {
      // Stop any currently playing audio before processing voice message
      stopCurrentAudio();

      // Create audio element immediately in user interaction context for mobile autoplay
      const audioPlaceholder = new Audio();
      audioPlaceholder.src = 'data:audio/mpeg;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA//////////////////////////////////////////////////////////////////8AAABQTEFNRTMuMTAwBLkAAAAAAAAAABUgJAUHQQABzAAABQGmzQb0AAAAAAD/+xDEAAPAAAGkAAAAIAAANIAAAARMQU1FMy4xMDBVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
      console.log('[handleAudioRecording] 📱 Created audio placeholder for mobile autoplay compatibility');

      setLoadingChat(true);
      setChatError(null);

      // Transcribe audio - include mime_type for proper backend handling
      const transcriptionResponse = await apiClient.transcribe({
        audio_base64: audioBase64,
        language: 'it',
        mime_type: recordingMimeTypeRef.current,
      });

      const transcribedText = transcriptionResponse.text.trim();

      if (!transcribedText) {
        setChatError(t('chat.audioNotRecognized'));
        setLoadingChat(false);
        return;
      }

      // Send transcribed text as message
      setInputValue(transcribedText);

      // Automatically send the message
      console.log('[handleAudioRecording] 📤 Sending transcribed message to AI:', transcribedText);
      const response = await chatService.sendMessage(transcribedText, i18n.language);

      // Add user message to UI
      addMessage({
        id: `msg-${Date.now()}`,
        role: 'user',
        content: transcribedText,
        timestamp: Date.now(),
      });

      // Add assistant message to UI
      addMessage(response);

      // Check if booking is ready - show modal if so
      if (response.booking_ready && response.booking_data) {
        console.log('[handleAudioRecording] ✅ Booking ready from voice input');
        setPendingBookingData(response.booking_data);
        setShowBookingModal(true);
      }

      // Synthesize audio and animate avatar
      try {
        console.log('[handleAudioRecording] 🎤 Starting TTS synthesis for text:', response.content.substring(0, 50) + '...');
        setAvatarLoading(true);

        const ttsResponse = await apiClient.synthesize({
          text: response.content,
          language: i18n.language,
        });

        console.log('[handleAudioRecording] ✅ TTS synthesis completed');
        console.log('[handleAudioRecording] - Audio received:', !!ttsResponse.audio_base64);
        console.log('[handleAudioRecording] - Visemes count:', ttsResponse.visemes?.length || 0);
        console.log('[handleAudioRecording] - Duration:', ttsResponse.duration);

        setAvatarLoading(false);
        setAvatarSpeaking(true);

        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          console.log('[handleAudioRecording] 📊 Visemes received:', ttsResponse.visemes.length);
          setCurrentVisemes(ttsResponse.visemes);
        } else {
          console.warn('[handleAudioRecording] ⚠️ No visemes received from TTS');
        }

        if (ttsResponse.audio_base64) {
          console.log('[handleAudioRecording] 🔊 Setting audio src and playing...');

          // Use the audio placeholder created in user interaction context
          audioPlaceholder.src = `data:audio/mpeg;base64,${ttsResponse.audio_base64}`;
          setAudioElement(audioPlaceholder);

          audioPlaceholder.onplay = () => {
            console.log('[handleAudioRecording] ▶️ Audio started playing (mobile-compatible)');
          };

          audioPlaceholder.onended = () => {
            console.log('[handleAudioRecording] ⏹️ Audio ended');
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audioPlaceholder.onerror = (error) => {
            console.error('[handleAudioRecording] ❌ Audio error:', error);
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          };

          audioPlaceholder.play().catch((error) => {
            console.error('[handleAudioRecording] ❌ Audio playback error:', error);
            setAvatarSpeaking(false);
            setCurrentVisemes([]);
            setAudioElement(null);
          });
        } else {
          console.warn('[handleAudioRecording] ⚠️ No audio_base64 in TTS response');
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
        }
      } catch (ttsError) {
        console.error('[handleAudioRecording] ❌ TTS error:', ttsError);
        setAvatarSpeaking(false);
      }
    } catch (error) {
      console.error('Transcription error:', error);
      setChatError(t('chat.transcriptionError'));
    } finally {
      setLoadingChat(false);
      setInputValue('');
    }
  };

  return (
    <div className={styles.chatWidget}>
      <div className={styles.chatHeader}>
        <h2>{t('chat.title')}</h2>
        <button
          className={styles.clearButton}
          onClick={handleClearChat}
          title={t('chat.clearButton')}
        >
          ↻
        </button>
      </div>

      {/* Messages Container */}
      <div className={styles.messagesContainer} ref={messagesContainerRef}>
        {messages.length === 0 && (
          <div className={styles.emptyState}>
            <p>{t('chat.emptyState1')}</p>
            <p>{t('chat.emptyState2')}</p>
            <p style={{
              marginTop: '20px',
              padding: '12px 20px',
              backgroundColor: '#e8f4f8',
              borderRadius: '8px',
              fontWeight: '600',
              color: '#1a3a52',
              fontSize: '15px'
            }}>
              💬 {t('chat.emptyStateSuggestion')}
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            isLoading={isLoadingChat && idx === messages.length - 1 && msg.role === 'assistant'}
          />
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Error Message */}
      {chatError && (
        <div className={styles.errorMessage}>
          <p>{chatError}</p>
        </div>
      )}

      {/* Input Form */}
      <form className={styles.inputForm} onSubmit={handleSendMessage}>
        <button
          type="button"
          onClick={() => {
            console.log('🎤 Mic button clicked, isRecording:', isRecording);
            if (isRecording) {
              stopRecording();
            } else {
              startRecording();
            }
          }}
          disabled={isLoadingChat}
          className={`${styles.micButton} ${isRecording ? styles.recording : ''}`}
          title={isRecording ? t('chat.stopRecording') : t('chat.startRecording')}
          aria-label={isRecording ? t('chat.stopRecording') : t('chat.startRecording')}
        >
          {isRecording ? '⛔' : '🎤'}
        </button>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={isRecording ? t('chat.inputPlaceholderRecording') : t('chat.inputPlaceholder')}
          disabled={isLoadingChat || isRecording}
          className={styles.input}
        />
        <button
          type="submit"
          disabled={isLoadingChat || !inputValue.trim() || isRecording}
          className={styles.sendButton}
        >
          {isLoadingChat ? '...' : '→'}
        </button>
      </form>

      {/* Booking Confirmation Modal - Using Portal to render outside parent container */}
      {showBookingModal && pendingBookingData && (() => {
        console.log('[ChatWidget] 🎬 Rendering modal via Portal to document.body');
        return ReactDOM.createPortal(
          <div className={styles.modalOverlay} onClick={() => console.log('[ChatWidget] Modal overlay clicked')}>
            <div className={styles.modal} onClick={(e) => { e.stopPropagation(); console.log('[ChatWidget] Modal content clicked'); }}>
              <h2>{t('booking.modal.title')}</h2>
            <div className={styles.bookingDetails}>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.boat')}:</span>
                <span className={styles.value}>{pendingBookingData.boat_model}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.length')}:</span>
                <span className={styles.value}>{pendingBookingData.boat_length}m</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.width')}:</span>
                <span className={styles.value}>{pendingBookingData.boat_width}m</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.draft')}:</span>
                <span className={styles.value}>{pendingBookingData.boat_draft}m</span>
              </div>
              <hr />
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.checkIn')}:</span>
                <span className={styles.value}>{formatDate(pendingBookingData.check_in_date)}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.checkOut')}:</span>
                <span className={styles.value}>{formatDate(pendingBookingData.check_out_date)}</span>
              </div>
              <hr />
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.name')}:</span>
                <span className={styles.value}>{pendingBookingData.contact_name}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.email')}:</span>
                <span className={styles.value}>{pendingBookingData.contact_email}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.label}>{t('booking.modal.phone')}:</span>
                <span className={styles.value}>{pendingBookingData.contact_phone}</span>
              </div>
              {pendingBookingData.notes && (
                <>
                  <hr />
                  <div className={styles.detailRow}>
                    <span className={styles.label}>{t('booking.modal.notes')}:</span>
                    <span className={styles.value}>{pendingBookingData.notes}</span>
                  </div>
                </>
              )}
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowBookingModal(false);
                  setPendingBookingData(null);
                }}
                disabled={isSubmittingBooking}
              >
                {t('booking.modal.cancel')}
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleConfirmBooking}
                disabled={isSubmittingBooking}
              >
                {isSubmittingBooking ? t('booking.modal.submitting') : t('booking.modal.confirm')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      );
      })()}
    </div>
  );
};
